import React, {
  useRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useEffect,
  useReducer,
  useId,
  useState,
} from 'react';
import type {
  ChatWidgetProps,
  ChatWidgetAPI,
  Attachment,
  ProductAction,
  ProductCard,
} from './types';
import { RECORD_VIEW_CONFIG, DEFAULT_RECORD_DATA } from './constants';
import { STORAGE_KEY } from './constants/storage';
import { exportConversation } from './utils/export';
import { validateFiles } from './utils/fileValidation';
import { isHostAllowed, isOriginAllowed } from './utils/domain';
import { isWithinAvailability } from './utils/availability';
import { isSafeUrl } from './utils/urlSafety';
import { sanitizeMessagesForStorage } from './utils/messageStorage';
import { useSessionHistory } from './hooks/useSessionHistory';
import { useAutoOpen } from './hooks/useAutoOpen';
import { useConsent } from './hooks/useConsent';
import { useAnalytics } from './hooks/useAnalytics';
import { useOpenState } from './hooks/useOpenState';
import { useThread } from './hooks/useThread';
import { useChat } from './hooks/useChat';
import { useTelemetry } from './hooks/useTelemetry';
import { useDragDrop } from './hooks/useDragDrop';
import { useLocalStorage } from './hooks/useLocalStorage';
import { ChatProvider } from './context/ChatContext';
import type { ChatContextValue } from './context/ChatContext';
import ErrorBoundary from './components/ErrorBoundary';
import MinimizedButton from './components/MinimizedButton';
import Header from './components/Header/Header';
import MainContent from './components/MainContent';
import InputArea from './components/InputArea/InputArea';
import PreChatForm from './components/PreChatForm';
import SessionPanel from './components/SessionPanel';
import TelemetryPanel from './components/TelemetryPanel';
import { getSessionId } from './utils/session';
import { useResolvedProps } from './hooks/useResolvedProps';
import { useScrollManagement } from './hooks/useScrollManagement';
import { useBusinessActions } from './hooks/useBusinessActions';
import { useKeyboardNavigation } from './hooks/useKeyboardNavigation';
import { useIntroMessage } from './hooks/useIntroMessage';
import { useRemoteSettings } from './hooks/useRemoteSettings';
import { loadHighlighter } from './utils/markdown';
import { uiReducer, initialUIState } from './reducers/uiReducer';

// ---- ChatWidgetInner ----
const ChatWidgetInner = React.forwardRef<ChatWidgetAPI, ChatWidgetProps>(
  function ChatWidgetInner(props, ref) {
    const { remoteSettings, isMobile } = useRemoteSettings({
      settingsEndpoint: props.settingsEndpoint || null,
      settingsApiKey: props.settingsApiKey ?? props.apiKey ?? null,
      onError: props.onError,
    });

    const resolved = useResolvedProps(props, remoteSettings);
    const {
      readSetting,
      companyName,
      companyLogo,
      agentName,
      agentAvatar,
      theme,
      darkMode,
      welcomeMessage,
      suggestedActions,
      placeholder,
      language,
      agentOrgId,
      agentId,
      kbCollection,
      kbApiKey,
      kbTopK,
      kbScoreThreshold,
      conversionPrompt,
      sessionHistoryEnabled,
      maxSessions,
      allowAttachments,
      allowVoice,
      allowVideo,
      apiKey,
      widgetAuthToken,
      apiBaseUrl,
      messageEndpoint,
      messageCreateEndpoint,
      streamEndpoint,
      threadEndpoint,
      threadCreateEndpoint,
      threadUpdateEndpoint,
      threadDeleteEndpoint,
      cartEndpoint,
      analyticsBaseUrl,
      analyticsEndpoint,
      analyticsSampleRate,
      analyticsEnabled,
      analyticsApiKey,
      telemetryEnabled,
      telemetryPollIntervalMs,
      telemetryShowContext,
      telemetryEndpoint,
      provider,
      enableStreaming,
      enableToolVisualization,
      enableRichMessages,
      enableSourceCitations,
      enableThinking,
      defaultOpen,
      subjectProp,
      channel,
      threadIdProp,
      threadPollIntervalMs,
      department,
      customFields,
      records,
      effectivePersistThread,
      position,
      zIndex,
      showViewTabs,
      showTimestamps,
      filteredBubbleOptions,
      maxInputLength,
      inputWarningThreshold,
      maxMessagesPerSession,
      rateLimit,
      maxFileSizeMB,
      maxFileCount,
      allowedFileTypes,
      allowedDomains,
      allowedOrigins,
      availability,
      offlineMessage,
      handoff,
      sessionConfig,
      autoOpenRules,
      autoOpenDelayMs,
      preChatForm,
      consent,
      doNotTrain,
      retentionDays,
      sessionVariables,
      retryPolicy,
      lazyLoad,
    } = resolved;

    const open = props.open;
    const onOpenChange = props.onOpenChange;
    const onSend = props.onSend;
    const onReceive = props.onReceive;
    const onError = props.onError;
    const onThreadCreate = props.onThreadCreate;
    const onThreadUpdate = props.onThreadUpdate;
    const onThreadDelete = props.onThreadDelete;
    const onMessageCreate = props.onMessageCreate;
    const onHandoffRequest = props.onHandoffRequest;
    const onVoiceStart = props.onVoiceStart;
    const onVideoStart = props.onVideoStart;
    const onProductAction = props.onProductAction;
    const buildMessagePayload = props.buildMessagePayload;
    const parseResponse = props.parseResponse;
    const buildThreadUrl = props.buildThreadUrl;
    const buildThreadRequest = props.buildThreadRequest;
    const buildThreadUpdateUrl = props.buildThreadUpdateUrl;
    const buildThreadDeleteUrl = props.buildThreadDeleteUrl;
    const parseThreadResponse = props.parseThreadResponse;
    const parseThreadCreateResponse = props.parseThreadCreateResponse;

    const [ui, uiDispatch] = useReducer(uiReducer, {
      ...initialUIState,
      settings: { ...initialUIState.settings, language },
    });
    const widgetId = useId();
    const titleId = `${widgetId}-title`;
    const tabsIdBase = `${widgetId}-tabs`;
    const activeTabId = `${tabsIdBase}-tab-${ui.view}`;
    const activePanelId = `${tabsIdBase}-panel-${ui.view}`;
    const dialogId = `${widgetId}-dialog`;

    const subject = subjectProp ?? '';

    const preChatEnabled = useMemo(() => {
      if (typeof preChatForm?.enabled === 'boolean') return preChatForm.enabled;
      return Array.isArray(preChatForm?.fields) && preChatForm.fields.length > 0;
    }, [preChatForm]);
    const [preChatData, setPreChatData] = useState<Record<string, string>>({});
    const [preChatComplete, setPreChatComplete] = useState<boolean>(() => !preChatEnabled);

    useEffect(() => {
      setPreChatComplete(!preChatEnabled);
    }, [preChatEnabled]);

    const consentRequired = useMemo(() => {
      if (typeof consent?.required === 'boolean') return consent.required;
      const flag = readSetting('security.consentRequired');
      return !!flag;
    }, [consent, readSetting]);
    const consentText = consent?.text || 'By starting this chat, you agree to our terms.';
    const consentLinkText = consent?.linkText || 'Learn more';
    const consentLinkUrl = consent?.linkUrl || '';
    const consentVersion = consent?.version || 'v1';
    const consentStorageKey = consent?.storageKey || 'rw_chat_consent';
    const consentRemember = consent?.remember !== false;

    const { hasConsent, setHasConsent } = useConsent({
      required: consentRequired,
      version: consentVersion,
      storageKey: consentStorageKey,
      remember: consentRemember,
    });

    const analyticsFields = useMemo(
      () => ({
        ...customFields,
        ...(sessionVariables ? { sessionVariables } : {}),
        ...(Object.keys(preChatData || {}).length > 0 ? { preChat: preChatData } : {}),
      }),
      [customFields, sessionVariables, preChatData]
    );

    // ---- Hooks ----
    const trackAnalytics = useAnalytics({
      analyticsBaseUrl,
      apiKey,
      customFields: analyticsFields,
      analyticsEnabled,
      analyticsSampleRate,
      analyticsEndpoint,
      analyticsApiKey: analyticsApiKey || undefined,
      sessionConfig,
    });

    const [domainAllowed, setDomainAllowed] = useState<boolean>(true);
    useEffect(() => {
      if (typeof window === 'undefined') return;
      const host = window.location.hostname;
      const origin = window.location.origin;
      const allowed =
        isHostAllowed(host, allowedDomains || [], []) &&
        isOriginAllowed(origin, allowedOrigins || []);
      setDomainAllowed(allowed);
      if (!allowed) {
        trackAnalytics('domain_blocked', { host, origin });
      }
    }, [allowedDomains, allowedOrigins, trackAnalytics]);

    const [isAvailable, setIsAvailable] = useState<boolean>(() =>
      isWithinAvailability(availability)
    );
    useEffect(() => {
      if (!availability || availability.enabled === false) {
        setIsAvailable(true);
        return;
      }
      const tick = () => setIsAvailable(isWithinAvailability(availability));
      tick();
      const id = setInterval(tick, 60_000);
      return () => clearInterval(id);
    }, [availability]);

    const { minimized, openChat, closeChat } = useOpenState({
      open,
      defaultOpen,
      onOpenChange,
      trackAnalytics,
    });
    const isMobileFullscreen = isMobile && !minimized;

    useEffect(() => {
      uiDispatch({ type: 'SET_FULLSCREEN', isFullscreen: isMobileFullscreen });
    }, [isMobileFullscreen, uiDispatch]);

    const agentMeta = useMemo(() => {
      const meta = {
        orgId: agentOrgId || undefined,
        agentId: agentId || undefined,
        kbCollection: kbCollection || undefined,
        kbApiKey: kbApiKey || undefined,
        kbTopK: typeof kbTopK === 'number' ? kbTopK : undefined,
        kbScoreThreshold: typeof kbScoreThreshold === 'number' ? kbScoreThreshold : undefined,
      };
      const hasValues = Object.values(meta).some(value => value !== undefined && value !== '');
      return hasValues ? meta : null;
    }, [agentOrgId, agentId, kbCollection, kbApiKey, kbTopK, kbScoreThreshold]);

    const metaExtras = useMemo(
      () => ({
        ...(Object.keys(preChatData || {}).length > 0 ? { preChat: preChatData } : {}),
        ...(consentRequired ? { consent: { accepted: hasConsent, version: consentVersion } } : {}),
        ...(doNotTrain !== undefined || retentionDays !== undefined
          ? { policy: { doNotTrain, retentionDays } }
          : {}),
        ...(sessionVariables ? { sessionVariables } : {}),
        ...(conversionPrompt ? { conversionPrompt } : {}),
        ...(agentMeta ? { agent: agentMeta } : {}),
      }),
      [
        preChatData,
        consentRequired,
        hasConsent,
        consentVersion,
        doNotTrain,
        retentionDays,
        sessionVariables,
        conversionPrompt,
        agentMeta,
      ]
    );

    // Use ref to bridge thread → chat setMessages (thread is initialized before chat)
    const chatSetMessagesRef = useRef<(msgs: import('./types').Message[]) => void>(() => {});

    const thread = useThread({
      threadIdProp,
      threadEndpoint,
      messageEndpoint,
      apiBaseUrl,
      apiKey,
      widgetAuthToken: widgetAuthToken || undefined,
      orgId: agentOrgId || undefined,
      agentId: agentId || undefined,
      provider,
      channel,
      threadCreateEndpoint,
      threadUpdateEndpoint,
      threadDeleteEndpoint,
      threadPollIntervalMs,
      persistThread: effectivePersistThread,
      lazyLoad,
      minimized,
      subject,
      department,
      customFields,
      metaExtras,
      sessionConfig,
      buildThreadUrl,
      buildThreadRequest,
      buildThreadUpdateUrl,
      buildThreadDeleteUrl,
      parseThreadResponse,
      parseThreadCreateResponse,
      onThreadCreate,
      onThreadUpdate,
      onThreadDelete,
      trackAnalytics,
      setMessages: msgs => {
        chatSetMessagesRef.current(msgs as import('./types').Message[]);
      },
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const telemetryHeaders = useMemo(() => thread.withAuthHeaders(), [thread.withAuthHeaders]);
    const telemetry = useTelemetry({
      enabled: telemetryEnabled,
      endpoint: telemetryEndpoint,
      pollIntervalMs: telemetryPollIntervalMs,
      headers: telemetryHeaders,
      orgId: agentOrgId || undefined,
      agentId: agentId || undefined,
    });

    const chat = useChat({
      enableStreaming,
      enableToolVisualization,
      enableRichMessages,
      enableSourceCitations,
      enableThinking,
      streamEndpoint,
      messageEndpoint,
      messageCreateEndpoint,
      apiBaseUrl,
      apiKey,
      provider,
      subject,
      department,
      channel,
      customFields,
      metaExtras,
      threadId: thread.threadId,
      threadBase: thread.threadBase,
      companyName,
      saveHistory: ui.settings.saveHistory,
      loadHistory: !sessionHistoryEnabled,
      buildMessagePayload,
      parseResponse,
      onSend,
      onReceive,
      onError,
      onMessageCreate,
      trackAnalytics,
      withAuthHeaders: thread.withAuthHeaders,
      fetchThread: thread.fetchThread,
      createThread: thread.createThread,
      threadCreateEndpoint,
      minimized,
      setThreadId: thread.setThreadId,
      sessionConfig,
      retryPolicy,
    });

    // Wire thread setMessages to chat's dispatcher
    chatSetMessagesRef.current = chat.setMessages;

    const sessionHistoryActive = sessionHistoryEnabled && ui.settings.saveHistory;
    const {
      sessionsOpen,
      setSessionsOpen,
      sessions,
      activeSessionId,
      createNewSession,
      selectSession,
      removeSession,
    } = useSessionHistory({
      active: sessionHistoryActive,
      maxSessions,
      setMessages: chat.setMessages,
      chatDispatch: chat.dispatch,
      setThreadId: thread.setThreadId,
      uiDispatch,
      messages: chat.state.messages,
      threadId: thread.threadId ?? null,
    });

    const sessionsPanelRef = useRef<HTMLDivElement>(null);
    const sessionsPreviousFocusRef = useRef<HTMLElement | null>(null);

    const openSessions = useCallback(() => {
      try {
        sessionsPreviousFocusRef.current = document.activeElement as HTMLElement | null;
      } catch {
        sessionsPreviousFocusRef.current = null;
      }
      setSessionsOpen(true);
    }, [setSessionsOpen]);

    const closeSessions = useCallback(() => {
      setSessionsOpen(false);
      // Restore focus to whatever opened the sessions panel, if possible.
      setTimeout(() => {
        sessionsPreviousFocusRef.current?.focus();
      }, 0);
    }, [setSessionsOpen]);

    const messagesForStorage = useMemo(
      () => sanitizeMessagesForStorage(chat.state.messages),
      [chat.state.messages]
    );

    // Debounced localStorage persistence
    useLocalStorage(
      STORAGE_KEY,
      messagesForStorage,
      ui.settings.saveHistory && !sessionHistoryEnabled,
      300
    );

    const {
      messagesEndRef,
      messagesContainerRef,
      scrollToBottomVisible,
      newMessagesWhileScrolledUp,
      handleMessagesScroll,
      scrollToBottom,
    } = useScrollManagement({
      messages: chat.state.messages,
      streamingMessage: chat.state.streamingMessage,
      activeSessionId,
      minimized,
    });

    const chatContainerRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const textAreaRef = useRef<HTMLTextAreaElement>(null);
    const rateLimitRef = useRef<number[]>([]);

    // Welcome/intro message
    useIntroMessage({
      historyLoaded: chat.historyLoaded,
      messagesLength: chat.state.messages.length,
      welcomeMessage: welcomeMessage || '',
      filteredBubbleOptions,
      activeSessionId,
      threadId: thread.threadId,
      dispatch: chat.dispatch,
    });

    // Focus composer when chat opens
    useEffect(() => {
      if (!minimized) {
        setTimeout(() => {
          try {
            textAreaRef.current?.focus();
          } catch {
            /* */
          }
        }, 50);
      }
    }, [minimized]);

    // Track widget load & preload highlighter
    useEffect(() => {
      trackAnalytics('widget_loaded', {});
      loadHighlighter();
    }, [trackAnalytics]);

    const autoOpenConfig = useMemo(() => {
      const hasAnyRule =
        typeof autoOpenDelayMs === 'number' ||
        typeof autoOpenRules?.delayMs === 'number' ||
        typeof autoOpenRules?.scrollPercent === 'number' ||
        !!autoOpenRules?.exitIntent ||
        typeof autoOpenRules?.idleMs === 'number';
      return {
        enabled: typeof autoOpenRules?.enabled === 'boolean' ? autoOpenRules.enabled : hasAnyRule,
        delayMs:
          typeof autoOpenRules?.delayMs === 'number' ? autoOpenRules.delayMs : autoOpenDelayMs,
        scrollPercent: autoOpenRules?.scrollPercent,
        exitIntent: autoOpenRules?.exitIntent,
        idleMs: autoOpenRules?.idleMs,
        maxOpensPerSession: autoOpenRules?.maxOpensPerSession ?? 1,
      };
    }, [autoOpenRules, autoOpenDelayMs]);

    // Auto-open rules
    useAutoOpen({
      controlledOpen: open,
      config: autoOpenConfig,
      minimized,
      domainAllowed,
      openChat,
      trackAnalytics,
    });

    // Keyboard navigation & focus trap
    useKeyboardNavigation({
      minimized,
      sessionsOpen,
      closeSessions,
      closeChat,
      chatContainerRef,
      sessionsPanelRef,
    });

    // Revoke object URLs on unmount
    useEffect(() => {
      return () => {
        ui.attachments.forEach(a => {
          if (a.previewUrl) URL.revokeObjectURL(a.previewUrl);
        });
      };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ---- Derived data ----
    const mergedRecords = useMemo(
      () => ({
        orders: records?.orders ?? DEFAULT_RECORD_DATA.orders,
        products: records?.products ?? DEFAULT_RECORD_DATA.products,
        subscriptions: records?.subscriptions ?? DEFAULT_RECORD_DATA.subscriptions,
        returns: records?.returns ?? DEFAULT_RECORD_DATA.returns,
        exchanges: records?.exchanges ?? DEFAULT_RECORD_DATA.exchanges,
      }),
      [records]
    );

    const viewCounts = useMemo(
      () => ({
        chat: chat.state.messages.length,
        orders: mergedRecords.orders?.length || 0,
        products: mergedRecords.products?.length || 0,
        subscriptions: mergedRecords.subscriptions?.length || 0,
        returns: mergedRecords.returns?.length || 0,
        exchanges: mergedRecords.exchanges?.length || 0,
      }),
      [chat.state.messages, mergedRecords]
    );

    const currentViewMode = useMemo(() => {
      const config = RECORD_VIEW_CONFIG[ui.view];
      if (!config) return undefined;
      return ui.viewModes[ui.view] ?? config.defaultMode ?? config?.modes?.[0]?.key ?? 'overview';
    }, [ui.view, ui.viewModes]);

    const showPreChat = preChatEnabled && !preChatComplete && domainAllowed;
    const needsConsent = consentRequired && !hasConsent;
    const isOffline = availability?.enabled && !isAvailable;
    const blockWhenOffline = !!availability?.blockWhenOffline;
    const userMessageCount = useMemo(
      () => chat.state.messages.filter(m => m.sender === 'user').length,
      [chat.state.messages]
    );
    const messageLimitReached =
      typeof maxMessagesPerSession === 'number' && maxMessagesPerSession > 0
        ? userMessageCount >= maxMessagesPerSession
        : false;
    const inputDisabled =
      !domainAllowed ||
      showPreChat ||
      needsConsent ||
      (isOffline && blockWhenOffline) ||
      messageLimitReached;
    const statusMessage = useMemo(() => {
      if (!domainAllowed) return 'Chat is disabled for this domain.';
      if (messageLimitReached) return 'You have reached the message limit for this session.';
      if (isOffline && blockWhenOffline && !offlineMessage) return 'We are currently offline.';
      return '';
    }, [domainAllowed, messageLimitReached, isOffline, blockWhenOffline, offlineMessage]);

    const fileValidationOptions = useMemo(
      () => ({
        allowedFileTypes,
        maxFileSize: typeof maxFileSizeMB === 'number' ? maxFileSizeMB * 1024 * 1024 : undefined,
        maxFileCount,
      }),
      [allowedFileTypes, maxFileSizeMB, maxFileCount]
    );

    // ---- Handlers ----
    const handleViewModeChange = useCallback(
      (viewKey: string, modeKey: string) => {
        uiDispatch({ type: 'SET_VIEW_MODE', viewKey, modeKey });
        trackAnalytics('view_mode_change', { view: viewKey, mode: modeKey });
      },
      [trackAnalytics]
    );

    const handleExport = useCallback(
      (format: string) => {
        exportConversation(chat.state.messages, format);
        trackAnalytics('conversation_exported', {
          format,
          messageCount: chat.state.messages.length,
        });
      },
      [chat.state.messages, trackAnalytics]
    );

    const handleFileUpload = useCallback(
      (fileList: FileList) => {
        if (!allowAttachments || inputDisabled) return;
        const files = Array.from(fileList || []);
        const { valid, errors } = validateFiles(
          files,
          ui.attachments.length,
          fileValidationOptions
        );
        if (errors.length) {
          console.warn(
            'File validation errors:',
            errors.map(e => e.reason)
          );
          uiDispatch({ type: 'SET_ATTACHMENT_ERRORS', errors: errors.map(e => e.reason) });
          trackAnalytics('file_validation_error', { count: errors.length });
        } else {
          uiDispatch({ type: 'CLEAR_ATTACHMENT_ERRORS' });
        }
        const mapped: Attachment[] = valid.map((file, idx) => ({
          id: `${Date.now()}-${idx}`,
          name: file.name,
          size: file.size,
          type: file.type,
          file,
          previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
        }));
        uiDispatch({ type: 'ADD_ATTACHMENTS', attachments: mapped });
      },
      [
        allowAttachments,
        inputDisabled,
        ui.attachments.length,
        trackAnalytics,
        fileValidationOptions,
      ]
    );

    const handleSend = useCallback(async () => {
      if (!domainAllowed) {
        chat.dispatch({ type: 'SEND_ERROR', error: 'Chat is disabled for this domain.' });
        return;
      }
      if (showPreChat) {
        chat.dispatch({
          type: 'SEND_ERROR',
          error: 'Complete the pre-chat form to start chatting.',
        });
        return;
      }
      if (needsConsent) {
        chat.dispatch({ type: 'SEND_ERROR', error: 'Please accept the consent to continue.' });
        return;
      }
      if (isOffline && blockWhenOffline) {
        chat.dispatch({ type: 'SEND_ERROR', error: offlineMessage || 'We are currently offline.' });
        return;
      }
      if (messageLimitReached) {
        chat.dispatch({
          type: 'SEND_ERROR',
          error: 'You have reached the message limit for this session.',
        });
        return;
      }

      if (rateLimit?.maxMessages && rateLimit?.windowMs) {
        const now = Date.now();
        const windowMs = rateLimit.windowMs;
        const maxMessages = rateLimit.maxMessages;
        rateLimitRef.current = rateLimitRef.current.filter(ts => now - ts < windowMs);
        if (rateLimitRef.current.length >= maxMessages) {
          const retryInMs = windowMs - (now - rateLimitRef.current[0]);
          const retryInSec = Math.ceil(retryInMs / 1000);
          chat.dispatch({
            type: 'SEND_ERROR',
            error: `You're sending too fast. Try again in ${retryInSec}s.`,
          });
          trackAnalytics('rate_limited', { retryInMs, maxMessages, windowMs });
          return;
        }
        rateLimitRef.current.push(now);
      }

      const atts = ui.attachments;
      uiDispatch({ type: 'CLEAR_ATTACHMENTS' });
      uiDispatch({ type: 'CLEAR_ATTACHMENT_ERRORS' });
      await chat.handleSend(atts);
      if (minimized) uiDispatch({ type: 'INCREMENT_UNREAD' });
    }, [
      domainAllowed,
      showPreChat,
      needsConsent,
      isOffline,
      blockWhenOffline,
      offlineMessage,
      messageLimitReached,
      rateLimit,
      chat,
      ui.attachments,
      minimized,
      trackAnalytics,
    ]);

    const { handleButtonClick, handleFormSubmit } = useBusinessActions({
      chatDispatch: chat.dispatch,
      chatLoading: chat.state.loading,
      createMessage: chat.createMessage,
      threadId: thread.threadId ?? null,
      createThread: thread.createThread,
      threadCreateEndpoint,
      subject,
      companyName,
      channel,
      allowAttachments,
      inputDisabled,
      fileInputRef,
      handleSend,
      onError,
    });

    const handleConsentToggle = useCallback(
      (checked: boolean) => {
        setHasConsent(checked);
        trackAnalytics(checked ? 'consent_accepted' : 'consent_revoked', {
          version: consentVersion,
        });
        if (checked) chat.dispatch({ type: 'CLEAR_ERROR' });
      },
      [setHasConsent, trackAnalytics, consentVersion, chat]
    );

    const handlePreChatSubmit = useCallback(
      (data: Record<string, string>) => {
        setPreChatData(data);
        setPreChatComplete(true);
        trackAnalytics('prechat_submitted', { fields: Object.keys(data) });
        chat.dispatch({ type: 'CLEAR_ERROR' });
      },
      [trackAnalytics, chat]
    );

    const handleHandoffClick = useCallback(() => {
      trackAnalytics('handoff_requested', { mode: handoff?.mode || 'message' });
      onHandoffRequest?.({ threadId: thread.threadId, preChatData });

      if (
        handoff?.mode === 'link' &&
        handoff?.url &&
        isSafeUrl(handoff.url) &&
        typeof window !== 'undefined'
      ) {
        const target = handoff.target || '_blank';
        window.open(handoff.url, target, 'noopener,noreferrer');
        return;
      }

      if (inputDisabled) {
        chat.dispatch({
          type: 'SEND_ERROR',
          error: 'Complete the required steps before requesting a handoff.',
        });
        return;
      }

      const message = handoff?.message || 'I would like to speak with a human.';
      chat.dispatch({ type: 'SET_INPUT', input: message });
      if (handoff?.autoSend) {
        setTimeout(() => {
          handleSend();
        }, 0);
      }
    }, [
      handoff,
      onHandoffRequest,
      thread.threadId,
      preChatData,
      chat,
      trackAnalytics,
      handleSend,
      inputDisabled,
    ]);

    const handleVoiceClick = useCallback(() => {
      trackAnalytics('voice_click', {});
      onVoiceStart?.();
    }, [trackAnalytics, onVoiceStart]);

    const handleVideoClick = useCallback(() => {
      trackAnalytics('video_click', {});
      onVideoStart?.();
    }, [trackAnalytics, onVideoStart]);

    const handleProductAction = useCallback(
      ({ action, product }: { action: ProductAction; product: ProductCard }) => {
        trackAnalytics('product_action', {
          action: action.type,
          label: action.label,
          productId: product.id,
        });
        if (onProductAction) {
          onProductAction({ action, product });
          return;
        }
        if (action.type === 'add_to_cart' && typeof window !== 'undefined') {
          const endpoint =
            cartEndpoint ||
            (apiBaseUrl ? `${apiBaseUrl.replace(/\/$/, '')}/cart/add` : '/api/cart/add');
          const variantId = action.value || product.id;
          if (variantId) {
            fetch(endpoint, {
              method: 'POST',
              headers: thread.withAuthHeaders(),
              body: JSON.stringify({
                variantId,
                quantity: 1,
                sessionId: getSessionId(sessionConfig),
              }),
            })
              .then(res => res.json())
              .then(payload => {
                if (payload?.checkoutUrl && isSafeUrl(payload.checkoutUrl)) {
                  window.open(payload.checkoutUrl, '_blank', 'noopener,noreferrer');
                }
              })
              .catch(err => {
                trackAnalytics('cart_error', { productId: product.id, error: String(err) });
              });
            return;
          }
        }
        if (action.url && isSafeUrl(action.url) && typeof window !== 'undefined') {
          window.open(action.url, '_blank', 'noopener,noreferrer');
        }
      },
      [trackAnalytics, onProductAction, cartEndpoint, apiBaseUrl, thread, sessionConfig]
    );

    // Drag and drop
    const { handleDragEnter, handleDragLeave, handleDragOver, handleDrop } = useDragDrop({
      containerRef: chatContainerRef,
      onDrop: handleFileUpload,
    });

    // ---- Imperative handle ----
    useImperativeHandle(ref, () => ({
      createThread: thread.createThread,
      updateThread: thread.updateThread,
      deleteThread: thread.deleteThread,
      createMessage: chat.createMessage,
      fetchThread: thread.fetchThread,
    }));

    // Cap rendered messages to last 100
    const displayMessages = useMemo(() => chat.state.messages.slice(-100), [chat.state.messages]);

    // Context value to eliminate prop drilling
    const chatContextValue: ChatContextValue = useMemo(
      () => ({
        messages: displayMessages,
        input: chat.state.input,
        loading: chat.state.loading,
        error: chat.state.error,
        isTyping: chat.state.isTyping,
        streamingMessage: chat.state.streamingMessage,
        activeToolCalls: chat.state.activeToolCalls,
        attachments: ui.attachments,
        view: ui.view,
        theme,
        enableToolVisualization,
        enableSourceCitations,
        enableThinking,
        showTimestamps,
        trackAnalytics,
      }),
      [
        displayMessages,
        chat.state.input,
        chat.state.loading,
        chat.state.error,
        chat.state.isTyping,
        chat.state.streamingMessage,
        chat.state.activeToolCalls,
        ui.attachments,
        ui.view,
        theme,
        enableToolVisualization,
        enableSourceCitations,
        enableThinking,
        showTimestamps,
        trackAnalytics,
      ]
    );

    const widgetPositionStyle = useMemo((): React.CSSProperties => {
      const style: React.CSSProperties = { position: 'fixed' };
      if (ui.isFullscreen) {
        style.top = 0;
        style.right = 0;
        style.bottom = 0;
        style.left = 0;
        return style;
      }
      switch (position) {
        case 'bottom-left':
          style.bottom = '1rem';
          style.left = '1rem';
          break;
        case 'top-left':
          style.top = '1rem';
          style.left = '1rem';
          break;
        case 'top-right':
          style.top = '1rem';
          style.right = '1rem';
          break;
        case 'bottom-right':
        default:
          style.bottom = '1rem';
          style.right = '1rem';
          break;
      }
      return style;
    }, [position, ui.isFullscreen]);

    // ---- Render ----
    return (
      <ChatProvider value={chatContextValue}>
        <div
          className="rw-widget"
          style={{ zIndex, overflowX: 'hidden', ...widgetPositionStyle }}
          data-rw-theme={darkMode}
          data-rw-mobile={isMobileFullscreen ? 'true' : 'false'}
        >
          {minimized ? (
            <MinimizedButton
              unreadCount={ui.unreadCount}
              theme={theme}
              onClick={openChat}
              controlsId={dialogId}
            />
          ) : (
            <div
              className={`flex flex-col rw-panel ${
                ui.isFullscreen ? 'w-full h-full' : 'w-96 h-[600px]'
              } bg-white rounded-2xl animate-slideUp overflow-x-hidden relative`}
              ref={chatContainerRef}
              id={dialogId}
              role="dialog"
              aria-modal="true"
              aria-label={`${companyName} chat`}
              aria-labelledby={titleId}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <Header
                view={ui.view}
                viewCounts={viewCounts}
                theme={theme}
                companyName={companyName}
                companyLogo={companyLogo}
                agentName={agentName}
                agentAvatar={agentAvatar}
                titleId={titleId}
                tabsIdBase={tabsIdBase}
                showViewTabs={showViewTabs}
                showSessionsButton={sessionHistoryActive}
                onSessionsClick={openSessions}
                onViewChange={(v: string) => uiDispatch({ type: 'SET_VIEW', view: v })}
                onMinimize={closeChat}
                onExport={handleExport}
              />

              {sessionHistoryActive && sessionsOpen && (
                <SessionPanel
                  sessions={sessions}
                  activeSessionId={activeSessionId}
                  containerRef={sessionsPanelRef}
                  onClose={closeSessions}
                  onCreate={() => {
                    createNewSession();
                    setTimeout(() => textAreaRef.current?.focus(), 0);
                  }}
                  onSelect={id => {
                    selectSession(id);
                    setTimeout(() => textAreaRef.current?.focus(), 0);
                  }}
                  onDelete={removeSession}
                />
              )}

              {showPreChat ? (
                <PreChatForm config={preChatForm} onSubmit={handlePreChatSubmit} />
              ) : (
                <>
                  <MainContent
                    view={ui.view}
                    viewMode={currentViewMode}
                    onModeChange={handleViewModeChange}
                    records={mergedRecords}
                    messages={displayMessages}
                    isTyping={chat.state.isTyping}
                    error={chat.state.error}
                    messagesEndRef={messagesEndRef}
                    messagesContainerRef={messagesContainerRef}
                    onMessagesScroll={handleMessagesScroll}
                    streamingMessage={chat.state.streamingMessage}
                    activeToolCalls={chat.state.activeToolCalls}
                    onButtonClick={handleButtonClick}
                    panelId={activePanelId}
                    labelledBy={showViewTabs ? activeTabId : titleId}
                    emptyStateMessage={welcomeMessage || undefined}
                    bubbleOptions={filteredBubbleOptions}
                    suggestedActions={suggestedActions}
                    onSuggestedAction={handleButtonClick}
                    onProductAction={handleProductAction}
                    onFormSubmit={handleFormSubmit}
                    showScrollToBottom={scrollToBottomVisible && ui.view === 'chat'}
                    newMessagesCount={newMessagesWhileScrolledUp}
                    onScrollToBottom={() => scrollToBottom('smooth')}
                  />

                  {telemetryEnabled && ui.view === 'chat' && (
                    <TelemetryPanel
                      data={telemetry.data}
                      loading={telemetry.loading}
                      error={telemetry.error}
                      showContext={telemetryShowContext}
                      onRefresh={telemetry.refresh}
                    />
                  )}

                  {isOffline && offlineMessage && (
                    <div className="px-4 py-2 text-xs text-amber-500 bg-gray-50 border-t border-gray-200">
                      {offlineMessage}
                    </div>
                  )}

                  {handoff?.enabled && (
                    <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                      <div className="text-xs text-gray-600">Need a human?</div>
                      <button
                        type="button"
                        onClick={handleHandoffClick}
                        className="text-xs px-2 py-1 rounded border border-blue-600 text-blue-600 hover:bg-blue-50 focus:outline-2 focus:outline-blue-500"
                      >
                        {handoff?.label || 'Talk to a human'}
                      </button>
                    </div>
                  )}

                  {consentRequired && (
                    <div className="px-4 py-2 text-xs text-gray-600 flex items-start gap-2 border-t border-gray-200">
                      <input
                        type="checkbox"
                        checked={hasConsent}
                        onChange={e => handleConsentToggle(e.target.checked)}
                        aria-label="Chat consent"
                        disabled={!domainAllowed}
                      />
                      <span>
                        {consentText}{' '}
                        {consentLinkUrl && (
                          <a
                            href={consentLinkUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-blue-500 hover:underline"
                          >
                            {consentLinkText}
                          </a>
                        )}
                      </span>
                    </div>
                  )}

                  {statusMessage && (
                    <div className="px-4 pb-2 text-xs text-gray-500">{statusMessage}</div>
                  )}

                  <InputArea
                    input={chat.state.input}
                    setInput={(v: string) => chat.dispatch({ type: 'SET_INPUT', input: v })}
                    handleSend={handleSend}
                    loading={chat.state.loading}
                    attachments={ui.attachments}
                    attachmentErrors={ui.attachmentErrors}
                    allowAttachments={allowAttachments}
                    allowVoice={allowVoice}
                    allowVideo={allowVideo}
                    theme={theme}
                    fileInputRef={fileInputRef}
                    textAreaRef={textAreaRef}
                    onFilesSelected={handleFileUpload}
                    onRemoveAttachment={(id: string) =>
                      uiDispatch({ type: 'REMOVE_ATTACHMENT', id })
                    }
                    placeholder={placeholder || 'Message...'}
                    onVoiceClick={handleVoiceClick}
                    onVideoClick={handleVideoClick}
                    disabled={inputDisabled}
                    maxInputLength={maxInputLength}
                    inputWarningThreshold={inputWarningThreshold}
                    allowedFileTypes={allowedFileTypes}
                  />
                </>
              )}
            </div>
          )}

          {/* Accessibility live region */}
          <div className="sr-only" aria-live="polite">
            {chat.state.loading && 'Loading...'}
            {chat.state.error && `Error: ${chat.state.error}`}
            {chat.state.isTyping && 'Agent is typing...'}
            {minimized &&
              ui.unreadCount > 0 &&
              `You have ${ui.unreadCount} unread message${ui.unreadCount === 1 ? '' : 's'}.`}
          </div>
        </div>
      </ChatProvider>
    );
  }
);

const ChatWidget = React.forwardRef<ChatWidgetAPI, ChatWidgetProps>(
  function ChatWidget(props, ref) {
    return (
      <ErrorBoundary>
        <ChatWidgetInner {...props} ref={ref} />
      </ErrorBoundary>
    );
  }
);

ChatWidget.displayName = 'ChatWidget';

export default ChatWidget;
